import { SetStateAction, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import VillageConsoleContext from "../../store/VillageConsoleContext";
import { Box, CircularProgress, Stack, TextField } from "@mui/material";
import BhpLoadingButton from "../../components/BhpLoadingButton/BhpLoadingButton";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { useVillageRules } from "../../hooks/useVillageRules";
import { VillageRulesDto } from "../../dtos/VillageRulesDto";
import { useUploadRules } from "./useUploadRules";
import { ConflictingVillageRulesVersion } from "./ConflictingVillageRulesVersion";
import ParseHtml from 'html-react-parser';
import SaveIcon from '@mui/icons-material/Save';
import { Domains } from "../../data/Domains";
import { UploadRulesParameters } from "./UploadRulesParameters";
import { getCurrentVillageRule } from "./uploadRules.utils";


export const UploadRules: React.FC = ( ) => {

    const [ cannotUploadRules, setCannotUploadRules ] = useState< boolean >( false );
    
    const [ canSubmit, setCanSubmit ] = useState< boolean >( false );
    const [ dialogOpen, setDialogOpen ] = useState< boolean >( false );
    const [ htmlText, setHtmlText ] = useState< string >( '' );
    const [ isLoading, setIsLoading ] = useState< boolean >( true );
    const [ isThirdParty, setIsThirdParty ] = useState< boolean >( false );
    const [ isUploading, setIsUploading ] = useState< boolean >( false );
    const [ startFrom, setStartFrom ] = useState< string >( '' );
    const [ version, setVersion ] = useState< string >( '' );
    const [ villageRules, setVillageRules ] = useState< VillageRulesDto[ ] >( [ ] );
    
    const navigate = useNavigate( ); 
    const  { uploadVillageRules } = useUploadRules( );
    const { getVillageRules } = useVillageRules( );
    const { domain } = useContext( VillageConsoleContext );


    useEffect(( ) => { initialize( domain ); }, [ domain ]);


    const calculateDates = ( ) => {

        let today = ( new Date() ).toISOString( ).slice( 0, 10 );
        setStartFrom( today );

    };


    const clearState = ( ) => {

        setCanSubmit( false );
        setIsThirdParty( false );
        setStartFrom( '' );
        setVersion( '' );

    };


    const setCurrentVillageRule = ( domain : Domains, villageRules: VillageRulesDto[ ], isThirdParty?: boolean ) => {

        const villageRule = getCurrentVillageRule( domain, villageRules, isThirdParty );

        if( villageRule ){

            setHtmlText( villageRule.ruleHtml );
            
        }

    };


    const handleCancel = ( ) => setDialogOpen( false );


    const handleOk = ( ) => {

        setDialogOpen( false );
        submit( );

    };

    
    const handleSelectCannotUploadRules = ( cannotUploadRules: SetStateAction< boolean > ) => {

        setCannotUploadRules( cannotUploadRules as boolean ); 

    }


    const handleSelectIsThirdParty = ( isThirdParty: SetStateAction< boolean > ) => {

        const villageRule = getCurrentVillageRule( domain, villageRules, isThirdParty as boolean );
        setIsThirdParty( isThirdParty as boolean ); 

        if( villageRule ){

            setHtmlText( villageRule.ruleHtml );
            
        }

    };


    const handleSelectVersion = ( version: SetStateAction< string > ) => {
        
        setCanSubmit( Boolean( version ) );

        if( version ){
            
            setVersion( version);

        } else {
            
            setVersion( '' );

        }


    }


    const initialize = async( domain: Domains ) => {

        clearState( );
        calculateDates( );

        setIsLoading( true );
        const villageRules: VillageRulesDto[] = await getVillageRules( domain );
        setVillageRules( villageRules );
        setCurrentVillageRule( domain, villageRules, isThirdParty );
        setIsLoading( false );

    }; 
    

    const submit = async (): Promise<void> => {

        setIsUploading( true );

        const isUpdated = await uploadVillageRules( domain, htmlText, startFrom, version, domain === Domains.Bma ? isThirdParty : undefined );

        setIsUploading( false );

        if( isUpdated ){

            navigate( '/search-rules' );

        }

    };


    return (

        <>
        
            {
                isLoading 
                    ?
                <Box 
                    display="flex" 
                    alignItems="center"
                    justifyContent="center"
                    marginTop={50}                
                >
                    <CircularProgress size="80px" sx={{ color: '#e65400' }}/>
                </Box>
                    :
                cannotUploadRules
                    ?
                <ConflictingVillageRulesVersion />
                    :
                <>
                    
                    <Stack direction="column" display="flex" justifyContent="center" gap={ 1 } sx={{ marginTop: '20px' }}>

                        <UploadRulesParameters 
                            isUploading={ isUploading } 
                            selectCannotUploadRules={ handleSelectCannotUploadRules }
                            selectIsThirdParty={ handleSelectIsThirdParty }
                            selectVersion={ handleSelectVersion }
                            villageRules={ villageRules } 
                        />

                    </Stack>

                    <table style={{ width: '100%' }}>
                        <tbody>
                            <tr>
                                <td style={{width: '50%'}}>
                                    <TextField
                                        disabled={isUploading}
                                        label="Village Rules Html"
                                        sx={{marginTop: '10px', width: '100%'}} 
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => setHtmlText( event.target.value ) }
                                        value={htmlText}
                                        multiline
                                        rows={30}/>
                                </td>
                                <td style={{ width: '50%', verticalAlign: 'top', height: '725px' }}>

                                    <div style={{ height: '725px', overflow: 'auto' }}>
                                        { ParseHtml( htmlText ) }
                                    </div>
                                        
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <hr/>
                
                    { 
                        
                        canSubmit 
                            &&
                        <div style={{ width:'100%', marginTop:'10px', display: 'flex', justifyContent: 'flex-end' }}>
                            <BhpLoadingButton
                                loading={ isUploading }
                                loadingPosition="start"
                                onClick={ ( ) => setDialogOpen( true ) }
                                startIcon={<SaveIcon />}
                                variant="contained"
                                sx={{ marginRight: '10px' }}
                            >
                                Submit
                            </BhpLoadingButton>
                        </div>

                    }

                    <ConfirmationDialog 
                        text={ `Please confirm that you want to upload new ${ domain } Village Rules` }
                        dialogOpen={ dialogOpen }
                        handleCancel={ handleCancel }
                        handleOk={ handleOk } 
                    />

                </>

            }

        </>
    
    );
    
};
